/**
 * Metadaten für alle Anlass-Landingpages: kanonische ID, Icon, Tone und die
 * URL-Slugs pro Sprache (für SEO-freundliche lokale URLs).
 *
 * Textinhalte stehen in useCasesContent.ts, gepflegt pro Sprache.
 */

import type { Icon } from "@phosphor-icons/react";
import {
  Baby,
  Cake,
  Campfire,
  Champagne,
  ChefHat,
  Coffee,
  Flame,
  GraduationCap,
  HandHeart,
  Heart,
  House,
  MaskHappy,
  Snowflake,
  Sun,
  TreeEvergreen,
  UsersThree,
} from "@phosphor-icons/react";

export type Tone =
  | "warm"
  | "fire"
  | "blue"
  | "green"
  | "pink"
  | "purple"
  | "gold"
  | "cool";

export type UseCase = {
  /** Kanonische sprachenunabhängige ID — gleichzeitig Lookup-Key in useCasesContent. */
  id: string;
  /** Slug pro Sprache — für SEO-freundliche URLs. Fallback: DE-slug. */
  slugs: Record<string, string>;
  icon: Icon;
  tone: Tone;
};

export const USE_CASES: UseCase[] = [
  {
    id: "hochzeit",
    slugs: { de: "hochzeit", en: "wedding", es: "boda" },
    icon: Heart,
    tone: "pink",
  },
  {
    id: "geburtstag",
    slugs: { de: "geburtstag", en: "birthday", es: "cumpleanos" },
    icon: Cake,
    tone: "warm",
  },
  {
    id: "grillen",
    slugs: { de: "grillen", en: "bbq", es: "barbacoa" },
    icon: Flame,
    tone: "fire",
  },
  {
    id: "brunch",
    slugs: { de: "brunch", en: "brunch", es: "brunch" },
    icon: Coffee,
    tone: "warm",
  },
  {
    id: "schule-kita",
    slugs: { de: "schule-kita", en: "school-daycare", es: "escuela-guarderia" },
    icon: GraduationCap,
    tone: "blue",
  },
  {
    id: "helferliste",
    slugs: { de: "helferliste", en: "volunteer-list", es: "voluntarios" },
    icon: HandHeart,
    tone: "green",
  },
  {
    id: "kuchenspende",
    slugs: { de: "kuchenspende", en: "cake-donation", es: "donacion-tartas" },
    icon: ChefHat,
    tone: "warm",
  },
  {
    id: "weihnachten",
    slugs: { de: "weihnachten", en: "christmas", es: "navidad" },
    icon: TreeEvergreen,
    tone: "green",
  },
  {
    id: "silvester",
    slugs: { de: "silvester", en: "new-years-eve", es: "nochevieja" },
    icon: Snowflake,
    tone: "cool",
  },
  {
    id: "jga",
    slugs: { de: "jga", en: "hen-stag", es: "despedida" },
    icon: Champagne,
    tone: "pink",
  },
  {
    id: "dorffest",
    slugs: { de: "dorffest", en: "village-fair", es: "fiesta-popular" },
    icon: Sun,
    tone: "warm",
  },
  {
    id: "camping",
    slugs: { de: "camping", en: "camping", es: "camping" },
    icon: Campfire,
    tone: "green",
  },
  {
    id: "taufe-kommunion",
    slugs: { de: "taufe-kommunion", en: "baptism-communion", es: "bautizo-comunion" },
    icon: Baby,
    tone: "cool",
  },
  {
    id: "karneval",
    slugs: { de: "karneval", en: "carnival", es: "carnaval" },
    icon: MaskHappy,
    tone: "purple",
  },
  {
    id: "einweihung",
    slugs: { de: "einweihung", en: "housewarming", es: "inauguracion" },
    icon: House,
    tone: "blue",
  },
  {
    id: "familienfest",
    slugs: { de: "familienfest", en: "family-gathering", es: "reunion-familiar" },
    icon: UsersThree,
    tone: "gold",
  },
];

/** Resolve beliebigen Slug aus beliebiger Sprache zu UseCase. */
export function findUseCaseBySlug(slug: string | undefined): UseCase | undefined {
  if (!slug) return undefined;
  return USE_CASES.find((uc) =>
    Object.values(uc.slugs).includes(slug),
  );
}

export function findUseCaseById(id: string | undefined): UseCase | undefined {
  if (!id) return undefined;
  return USE_CASES.find((uc) => uc.id === id);
}

/** Liefert den sprachspezifischen URL-Slug für einen Use-Case. */
export function slugFor(uc: UseCase, lang: string): string {
  const key = lang.slice(0, 2);
  return uc.slugs[key] ?? uc.slugs.de;
}

/** Die auf der Startseite beworbenen sechs Anlässe. */
export const HOME_USE_CASE_IDS: string[] = [
  "hochzeit",
  "geburtstag",
  "grillen",
  "brunch",
  "schule-kita",
  "helferliste",
];
